import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import type { DepartureCity } from '@/shared/model';
import { DEPARTURE_CITY_TEXTS } from '../config/texts';
import { sortCities } from '../lib/sortCities';

export interface UseDepartureCity {
  inputString: Ref<string>;
  selection: Ref<DepartureCity | null>;
  controlActivated: Ref<boolean>;
  invalid: Ref<boolean>;
  errorText: Ref<string>;

  optionsList: ComputedRef<DepartureCity[]>;
  label: ComputedRef<string>;
  clearVisible: ComputedRef<boolean>;

  open: () => void;
  close: () => void;
  setInput: (v: string) => void;
  selectCity: (city: DepartureCity) => void;
  clear: () => void;
  validate: () => boolean;
  setCities: (list: DepartureCity[]) => void;
}

export interface UseDepartureCityOptions {
  model: Ref<DepartureCity | null>;
  initialCities?: DepartureCity[];
}

export function useDepartureCity(options: UseDepartureCityOptions): UseDepartureCity {
  const { model } = options;
  const inputString = ref(model.value?.name ?? '');
  const controlActivated = ref(false);
  const invalid = ref(false);
  const errorText = ref('');
  const cities = ref<DepartureCity[]>(options.initialCities ?? []);

  const optionsList = computed<DepartureCity[]>(() => {
    const sorted = sortCities(cities.value);
    const query = inputString.value.trim().toLowerCase();
    if (query === '') {
      return sorted;
    }
    return sorted.filter((c) => c.name.toLowerCase().includes(query));
  });

  const label = computed<string>(() => model.value?.name ?? '');

  const clearVisible = computed<boolean>(() => controlActivated.value && inputString.value !== '');

  const open = (): void => {
    controlActivated.value = true;
    inputString.value = '';
  };

  const close = (): void => {
    controlActivated.value = false;
    invalid.value = false;
    errorText.value = '';
    inputString.value = label.value;
  };

  const setInput = (v: string): void => {
    inputString.value = v;
  };

  const selectCity = (city: DepartureCity): void => {
    model.value = city;
    inputString.value = city.name;
    controlActivated.value = false;
    invalid.value = false;
    errorText.value = '';
  };

  const clear = (): void => {
    inputString.value = '';
    invalid.value = false;
    errorText.value = '';
  };

  const validate = (): boolean => {
    if (model.value === null) {
      invalid.value = true;
      errorText.value = DEPARTURE_CITY_TEXTS.errorEmpty;
      return false;
    }
    invalid.value = false;
    errorText.value = '';
    return true;
  };

  const setCities = (list: DepartureCity[]): void => {
    cities.value = list;
  };

  watch(model, (city) => {
    if (!controlActivated.value) {
      inputString.value = city?.name ?? '';
    }
  });

  return {
    inputString,
    selection: model,
    controlActivated,
    invalid,
    errorText,
    optionsList,
    label,
    clearVisible,
    open,
    close,
    setInput,
    selectCity,
    clear,
    validate,
    setCities,
  };
}
